import { supabase } from "@/integrations/supabase/client";

/**
 * Invokes a Supabase Edge Function with automatic session refresh.
 * Always refreshes the session first to ensure a valid JWT token.
 * If the session cannot be refreshed, returns a user-friendly error.
 */
export async function invokeWithAuth<T = unknown>(
  functionName: string,
  options?: { body?: FormData | Record<string, unknown> }
): Promise<{ data: T; error: null } | { data: null; error: Error }> {
  // Always refresh the session to guarantee a valid, non-expired token
  const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

  if (refreshError || !session) {
    // If refresh fails, try getSession as fallback (might still be valid)
    const { data: { session: fallbackSession } } = await supabase.auth.getSession();
    if (!fallbackSession) {
      return {
        data: null,
        error: new Error("Sessão expirada. Faça login novamente."),
      };
    }
  }

  const { data, error } = await supabase.functions.invoke<T>(functionName, {
    body: options?.body,
  });

  if (error) {
    // If it's still a JWT error after refresh, the session is truly expired
    if (error.message?.includes("JWT") || error.message?.includes("token") || error.message?.includes("non-2xx")) {
      // One more attempt with a forced refresh
      const { data: { session: retrySession }, error: retryRefreshError } =
        await supabase.auth.refreshSession();

      if (retryRefreshError || !retrySession) {
        return {
          data: null,
          error: new Error("Sessão expirada. Faça login novamente."),
        };
      }

      const { data: retryData, error: retryError } = await supabase.functions.invoke<T>(
        functionName,
        { body: options?.body }
      );

      if (retryError) {
        return { data: null, error: retryError };
      }

      return { data: retryData as T, error: null };
    }

    return { data: null, error };
  }

  return { data: data as T, error: null };
}
